import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Axis type for scroll monitoring.
 */
type Axis = 'x' | 'y';

type Options = {
  /**
   * Axis to monitor for scroll position changes. Default is 'y'.
   */
  axis?: Axis;
  /**
   * Threshold in pixels to determine when to trigger scroll position changes.
   * Treats small scroll movements as no movement to avoid flickering.
   * Default is 1.
   */
  threshold?: number;
};

const readPosition = (element: HTMLElement, axis: Axis) => {
  return axis === 'y' ? element.scrollTop : element.scrollLeft;
};

const getMaxScrollFor = (element: HTMLElement, axis: Axis) => {
  return axis === 'y'
    ? element.scrollHeight - element.clientHeight
    : element.scrollWidth - element.clientWidth;
};

export const useScrollBoundary = (ref: RefObject<HTMLElement | null>, options: Options = {}) => {
  const { axis = 'y', threshold = 1 } = options;

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const rafRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);

  const measureAndUpdate = useCallback(
    (element: HTMLElement) => {
      const position = readPosition(element, axis);
      const max = Math.max(0, getMaxScrollFor(element, axis));

      const atStart = position <= threshold;
      const atEnd = position >= max - threshold;

      setIsAtStart(atStart);
      setIsAtEnd(atEnd);
    },
    [axis, threshold],
  );

  const scheduleUpdate = useCallback(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      measureAndUpdate(element);
    });
  }, [measureAndUpdate]);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    measureAndUpdate(element);
  }, [axis, measureAndUpdate]);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    element.addEventListener('scroll', scheduleUpdate, { passive: true });

    const observer = new ResizeObserver(() => {
      measureAndUpdate(element);
    });

    observer.observe(element);

    return () => {
      element.removeEventListener('scroll', scheduleUpdate);
      observer.disconnect();

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [scheduleUpdate, measureAndUpdate, axis]);

  return { isAtStart, isAtEnd };
};
